import { supabase } from '../lib/supabaseClient';
import { User, Session } from '@supabase/supabase-js';
import { profileService } from './profileService';

export interface AuthResult {
    success: boolean;
    error?: string;
    errorCode?: string;
    user?: User | null;
    session?: Session | null;
}

export type { UserProfile } from '../models/account';
const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024;
const AVATAR_EXTENSIONS_BY_MIME: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
};

export const AVATAR_ACCEPT = Object.keys(AVATAR_EXTENSIONS_BY_MIME).join(',');
export const validateAvatarFile = (file: File): string | null => {
    if (!AVATAR_EXTENSIONS_BY_MIME[file.type]) return 'Please choose a JPEG, PNG, WebP, or GIF image.';
    if (file.size <= 0 || file.size > MAX_AVATAR_SIZE_BYTES) return 'Profile images must be smaller than 5 MB.';
    return null;
};

const isDuplicateSignupError = (message: string): boolean => {
    const normalizedMessage = message.toLowerCase();
    return normalizedMessage.includes('already registered') ||
        normalizedMessage.includes('already been registered') ||
        normalizedMessage.includes('user already exists');
};

export const authErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
    const authError = error as { code?: string; message?: string; status?: number } | null;
    if (authError?.status === 429 || authError?.code?.includes('rate_limit')) {
        return 'Too many attempts. Please wait a few minutes before trying again.';
    }
    if (authError?.code === 'email_not_confirmed') return 'Please confirm your email before signing in.';
    if (authError?.code === 'signup_disabled') return 'New registrations are temporarily unavailable. Please try again later.';
    if (authError?.code === 'provider_disabled' || authError?.message?.includes('provider is not enabled')) {
        return 'This sign-in option is not available yet. Please use email or try again later.';
    }
    return authError?.message || fallback;
};

// Callback errors can arrive in either the query or hash. Never display tokens.
export const getAuthCallbackError = (): string => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (![query, hash].some(params => params.has('error') || params.has('error_description') || params.has('error_code'))) return '';
    return 'The sign-in or email link could not be completed. It may have expired or been cancelled. Please try again or request a new email.';
};

export const authService = {

    // Sign up with email + password
    signUp: async (
        email: string, password: string,
        fullName?: string, phone?: string,
        company?: string, role?: string, bio?: string
    ): Promise<AuthResult> => {
        const { data, error } = await supabase.auth.signUp({
            email: email.trim(),
            password,
            options: {
                data: {
                    full_name: fullName || '',
                    phone: phone || '',
                    company: company || '',
                    role: role || '',
                    bio: bio || '',
                },
                emailRedirectTo: window.location.origin + '/auth',
            }
        });

        if (error) {
            // Do not reveal whether an email address already has an account.
            // The signup screen will show the same confirmation step either way.
            if (isDuplicateSignupError(error.message)) {
                return { success: true };
            }
            return { success: false, error: authErrorMessage(error), errorCode: error.code };
        }

        // Supabase may mask an existing account with identities=[]. Keep the
        // same confirmation screen without disclosing account existence.
        if (data.user && data.user.identities && data.user.identities.length === 0) {
            return { success: true };
        }

        // Profile fields live in auth metadata until email confirmation. The
        // auth-user database trigger can therefore create the complete profile
        // even when signUp intentionally returns no session and RLS is active.
        return { success: true, user: data.user, session: data.session };
    },

    // Resend confirmation email (for users who signed up but didn't verify)
    resendConfirmation: async (email: string): Promise<AuthResult> => {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email: email.trim(),
            options: {
                emailRedirectTo: window.location.origin + '/auth',
            },
        });
        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true };
    },

    // Verify OTP code (6-digit code sent via email)
    verifyOtp: async (email: string, token: string): Promise<AuthResult> => {
        const { data, error } = await supabase.auth.verifyOtp({
            email: email.trim(),
            token,
            type: 'signup',
        });
        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true, user: data.user, session: data.session };
    },

    // Login with email + password
    signIn: async (email: string, password: string): Promise<AuthResult> => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
        });

        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true, user: data.user, session: data.session };
    },

    // Login with Google
    signInWithGoogle: async (): Promise<void> => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin + '/auth',
            }
        });
        if (error) throw error;
    },

    // Login with GitHub
    signInWithGithub: async (): Promise<void> => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'github',
            options: {
                redirectTo: window.location.origin + '/auth',
            }
        });
        if (error) throw error;
    },

    // Sign out
    signOut: async (): Promise<void> => {
        const { error } = await supabase.auth.signOut({ scope: 'local' });
        if (error) throw error;
    },

    // Get current session
    getSession: async (): Promise<Session | null> => {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        return data.session;
    },

    // Get current user
    getUser: async (): Promise<User | null> => {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        return data.user;
    },

    // Compatibility read facade; profile mutations belong to profileService.
    getProfile: profileService.get,

    changeEmail: async (email: string): Promise<AuthResult> => {
        const { data, error } = await supabase.auth.updateUser({ email: email.trim() }, {
            emailRedirectTo: window.location.origin + '/dashboard/account/security',
        });
        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true, user: data.user };
    },

    signOutOtherDevices: async (): Promise<void> => {
        const { error } = await supabase.auth.signOut({ scope: 'others' });
        if (error) throw error;
    },

    // Password reset
    resetPassword: async (email: string): Promise<AuthResult> => {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
            redirectTo: window.location.origin + '/reset-password',
        });

        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true };
    },

    updatePassword: async (password: string): Promise<AuthResult> => {
        if (password.length < 8) return { success: false, error: 'Password must be at least 8 characters.' };
        const { data, error } = await supabase.auth.updateUser({ password });
        if (error) return { success: false, error: authErrorMessage(error), errorCode: error.code };
        return { success: true, user: data.user };
    },

    // Listen to auth state changes
    onAuthStateChange: (callback: (event: string, session: Session | null) => void) => {
        return supabase.auth.onAuthStateChange(callback);
    },
};
